import styled, { css, keyframes } from 'styled-components';
import { colorSchemePreserveAttrs } from 'src/utils/colorScheme';
import { memMarkdownContent } from '../MemMarkdown/styles';

const TEXT_LINE_HEIGHT = 1.45;

const CARD_WIDTH = {
  textOnly: { min: '200px', max: '280px' },
  image: { min: '180px', max: '300px' },
  imageFallback: { min: '190px', max: '230px' },
};
const CARD_HEIGHT = {
  image: { min: '120px', max: '320px' },
  imageFallback: { min: '220px' },
};
/** Lines of text a card shows before clipping. */
const CLAMPED_LINES = { textOnly: 4, withImage: 3 };

export const Card = styled.div<{
  $hasImage?: boolean;
}>(
  ({ theme, $hasImage }) => css`
    background-color: ${theme.co.neutral99};
    border: 1px solid ${theme.co.neutral90};
    border-radius: ${theme.sz.s2};
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;

    ${
      !$hasImage &&
      css`
        padding: ${theme.sz.s3};
        gap: ${theme.sz.s2};
        min-width: ${CARD_WIDTH.textOnly.min};
        max-width: ${CARD_WIDTH.textOnly.max};
      `
    }

    ${
      $hasImage &&
      css`
        min-width: ${CARD_WIDTH.image.min};
        max-width: ${CARD_WIDTH.image.max};
      `
    }
  `,
);

export const ImageBox = styled.div.attrs(colorSchemePreserveAttrs)<{
  $imageUrl?: string;
  $imageX?: number;
  $imageY?: number;
  $aspectRatio?: string | null;
}>(
  ({ theme, $imageUrl, $imageX = 50, $imageY = 50, $aspectRatio }) => css`
    flex-shrink: 0;
    background-color: ${theme.co.neutral95};
    background-image: url('${$imageUrl}');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: ${$imageX}% ${$imageY}%;

    /* Use natural aspect ratio with intelligent sizing */
    ${
      $aspectRatio
        ? css`
            aspect-ratio: ${$aspectRatio};
            min-height: ${CARD_HEIGHT.image.min};
            max-height: ${CARD_HEIGHT.image.max};
          `
        : css`
            /* Fallback for when aspect ratio isn't available */
            min-height: ${CARD_HEIGHT.imageFallback.min};
          `
    }
  `,
);

export const Content = styled.div<{ $hasImage?: boolean }>(
  ({ theme, $hasImage }) => css`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: ${theme.sz.s2};
    padding: ${theme.sz.s3};
    ${
      $hasImage &&
      css`
        border-top: 1px solid ${theme.co.neutral90};
      `
    }
  `,
);

export const MemText = styled.div<{
  $hasImage?: boolean;
  $align?: string;
  $fontSize?: number;
  $isExpanded?: boolean;
}>(
  ({ theme, $hasImage, $align, $fontSize, $isExpanded }) => css`
    ${theme.typography.paragraph.s};
    ${memMarkdownContent(theme)};
    color: ${theme.co.neutral10};
    margin: 0;
    line-height: ${TEXT_LINE_HEIGHT};
    overflow: hidden;
    text-align: ${$align ?? 'left'};
    font-size: ${$fontSize ?? 1}rem;
    ${
      !$isExpanded &&
      css`
        max-height: ${
          ($hasImage ? CLAMPED_LINES.withImage : CLAMPED_LINES.textOnly) * TEXT_LINE_HEIGHT
        }em;
      `
    }
  `,
);

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const ImageSkeleton = styled.div(
  ({ theme }) => css`
    flex: 1;
    min-height: 160px;
    border-radius: ${theme.sz.s1};
    background: linear-gradient(
      90deg,
      ${theme.co.neutral90} 25%,
      ${theme.co.neutral95} 50%,
      ${theme.co.neutral90} 75%
    );
    background-size: 800px 100%;
    animation: ${shimmer} 1.5s infinite linear;
    position: relative;
    z-index: 1;
  `,
);

export const ActionsRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.sz.s2};
    margin-top: auto;
  `,
);

export const VoteGroup = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s1};
    margin-right: auto;
  `,
);

export const VoteCount = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.neutral30};
    min-width: ${theme.sz.s4};
    text-align: center;
  `,
);

export const ActionButton = styled.button<{ $active?: boolean }>(
  ({ theme, $active }) => css`
    background: none;
    border: none;
    padding: ${theme.sz.s1};
    cursor: pointer;
    border-radius: ${theme.sz.s1};
    color: ${$active ? theme.co.primary30 : theme.co.neutral40};
    font-size: ${theme.sz.s4};
    display: flex;
    align-items: center;
    line-height: 1;

    &:hover:not(:disabled) {
      background-color: ${theme.co.neutral95};
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  `,
);

export const EditModal = styled.div(
  ({ theme }) => css`
    padding: ${theme.sz.s6};
    box-sizing: border-box;
  `,
);

export const ModalTitle = styled.h2(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.s};
    color: ${theme.co.neutral10};
    margin: 0 0 ${theme.sz.s4} 0;
    padding-right: ${theme.sz.s8};
  `,
);

export const TextArea = styled.textarea(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    width: 100%;
    border: 1px solid ${theme.co.neutral80};
    border-radius: ${theme.sz.s1};
    padding: ${theme.sz.s2};
    resize: vertical;
    min-height: 80px;
    color: ${theme.co.neutral10};
    background-color: ${theme.co.neutral99};
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: ${theme.co.primary50};
    }
  `,
);

export const TextPreview = styled.div(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    ${memMarkdownContent(theme)};
    padding: ${theme.sz.s2};
    margin-top: ${theme.sz.s1};
    background-color: ${theme.co.neutral95};
    border: 1px solid ${theme.co.neutral80};
    border-radius: ${theme.sz.s1};
    color: ${theme.co.neutral20};
    min-height: 24px;
    line-height: 1.4;
  `,
);

export const CharCount = styled.span<{ $overLimit: boolean }>(
  ({ theme, $overLimit }) => css`
    ${theme.typography.paragraph.s};
    color: ${$overLimit ? theme.co.error40 : theme.co.neutral40};
    text-align: right;
    display: block;
    margin-top: ${theme.sz.s1};
  `,
);

export const FormatToolbar = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s1};
    flex-wrap: wrap;
    margin-bottom: ${theme.sz.s2};
  `,
);

export const FormatButton = styled.button<{ $active?: boolean }>(
  ({ theme, $active }) => css`
    ${theme.typography.paragraph.s};
    background: ${$active ? theme.co.primary95 : 'none'};
    border: 1px solid ${$active ? theme.co.primary70 : theme.co.neutral80};
    border-radius: ${theme.sz.s1};
    padding: ${theme.sz.s1};
    cursor: pointer;
    color: ${$active ? theme.co.primary30 : theme.co.neutral30};
    font-size: 0.875rem;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${theme.sz.s5};
    min-height: ${theme.sz.s5};

    &:hover {
      border-color: ${theme.co.primary70};
      color: ${theme.co.primary30};
    }
  `,
);

export const FormatDivider = styled.span(
  ({ theme }) => css`
    width: 1px;
    height: ${theme.sz.s4};
    background: ${theme.co.neutral80};
    margin: 0 ${theme.sz.s1};
    flex-shrink: 0;
  `,
);

export const SwitchRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.sz.s2};
    ${theme.typography.paragraph.s};
    color: ${theme.co.neutral20};
    margin-top: ${theme.sz.s2};
  `,
);

export const ModalButtons = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s2};
    justify-content: flex-end;
    margin-top: ${theme.sz.s4};
  `,
);

export const ImageSection = styled.div(
  ({ theme }) => css`
    margin: ${theme.sz.s3} 0;
    padding: ${theme.sz.s3};
    border: 1px solid ${theme.co.neutral80};
    border-radius: ${theme.sz.s1};
    background-color: ${theme.co.neutral98};
  `,
);

export const ImageSectionTitle = styled.h3(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.neutral30};
    margin: 0 0 ${theme.sz.s2} 0;
    font-weight: 600;
  `,
);

export const ImageRow = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s2};
    align-items: flex-start;
  `,
);

export const ImagePreviewCard = styled.div.attrs(colorSchemePreserveAttrs)<{
  $imageUrl?: string;
  $imageX?: number;
  $imageY?: number;
  $aspectRatio?: string | null;
}>(
  ({ theme, $imageUrl, $imageX = 50, $imageY = 50, $aspectRatio }) => css`
    border: 1px solid ${theme.co.neutral80};
    border-radius: ${theme.sz.s1};
    background-size: contain;
    background-repeat: no-repeat;
    background-position: ${$imageX}% ${$imageY}%;
    background-image: url('${$imageUrl}');
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: ${theme.sz.s3};

    /* Use same natural aspect ratio approach as gallery */
    ${
      $aspectRatio
        ? css`
            aspect-ratio: ${$aspectRatio};
            min-width: 200px;
            max-width: 400px;
            min-height: 150px;
            max-height: 500px;
          `
        : css`
            /* Fallback for when aspect ratio isn't available */
            width: 240px;
            height: 300px;
          `
    }
  `,
);

export const ImagePreviewText = styled.div<{
  $align?: string;
  $position?: string;
  $fontSize?: number;
}>(
  ({ theme, $align, $position, $fontSize }) => css`
    /* Match MemText styling exactly for images */
    ${theme.typography.paragraph.s};
    ${memMarkdownContent(theme)};
    color: ${theme.co.neutral99};
    margin: 0;
    line-height: ${TEXT_LINE_HEIGHT};
    max-height: ${CLAMPED_LINES.withImage * TEXT_LINE_HEIGHT}em;
    overflow: hidden;
    position: relative;
    z-index: 1;
    text-align: ${$align ?? 'left'};
    font-size: ${$fontSize ?? 1}rem;
    text-shadow:
      -1px -1px 0 rgba(0, 0, 0, 0.9),
      1px -1px 0 rgba(0, 0, 0, 0.9),
      -1px 1px 0 rgba(0, 0, 0, 0.9),
      1px 1px 0 rgba(0, 0, 0, 0.9);

    ${
      $position === 'top' &&
      css`
        margin-bottom: auto;
      `
    }
    ${
      (!$position || $position === 'bottom') &&
      css`
        margin-top: auto;
      `
    }
    ${
      $position === 'middle' &&
      css`
        margin-top: auto;
        margin-bottom: auto;
      `
    }
  `,
);

export const VerticalButtons = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s1};
  `,
);

export const HorizontalControls = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s2};
    margin-top: ${theme.sz.s2};
    align-items: center;
  `,
);

export const ControlGroup = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s1};
  `,
);
